import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, ChevronDown, ChevronRight, Wrench, DollarSign, TrendingUp, TrendingDown, BellRing, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAINT_TYPES = ["Oil Change", "Repair", "Tires", "Cleaning", "Inspection", "Other"];

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export default function LedgerManager({ token, onAuthError }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => {
    setLoading(true);
    axios.get(`${API}/ledger`, auth).then((r) => setData(r.data))
      .catch((e) => { if (e.response?.status === 401) onAuthError?.(); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  if (!data) return <p className="text-white/40">Could not load ledger.</p>;

  const t = data.totals;
  return (
    <div data-testid="admin-ledger">
      <p className="text-white/50 text-sm mb-4">Revenue, costs and profit per vehicle. Profit = Revenue (collected) − Maintenance − Lost revenue − Purchase price. Set each car's purchase price in the Fleet tab.</p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <Stat label="Revenue" value={money(t.revenue)} testid="ledger-total-revenue" tone="ok" icon={DollarSign} />
        <Stat label="Maintenance" value={money(t.maintenance_cost)} testid="ledger-total-maint" tone="warn" icon={Wrench} />
        <Stat label="Lost Revenue" value={money(t.lost_revenue)} testid="ledger-total-lost" tone="warn" icon={TrendingDown} />
        <Stat label="Purchase Cost" value={money(t.purchase_price)} testid="ledger-total-purchase" icon={DollarSign} />
        <Stat label="Net Profit" value={money(t.profit)} testid="ledger-total-profit" tone={t.profit >= 0 ? "ok" : "danger"} icon={TrendingUp} />
      </div>

      <div className="space-y-2">
        {data.vehicles.map((v) => (
          <div key={v.id} data-testid={`ledger-row-${v.id}`} className="bg-[#0C0C0C] border border-white/10 rounded-lg overflow-hidden">
            <button onClick={() => setOpenId(openId === v.id ? null : v.id)} className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors">
              <div className="flex items-center gap-2 min-w-0">
                {openId === v.id ? <ChevronDown size={16} className="text-white/40 shrink-0" /> : <ChevronRight size={16} className="text-white/40 shrink-0" />}
                <span className="font-semibold truncate">{v.year} {v.make} {v.model}</span>
              </div>
              <div className="flex items-center gap-4 text-sm shrink-0">
                <span className="text-green-400 hidden sm:inline">{money(v.revenue)} rev</span>
                <span className="text-yellow-400 hidden md:inline">−{money(v.maintenance_cost + v.lost_revenue)} cost</span>
                <span className={`font-display font-bold ${v.profit >= 0 ? "text-[#F2B705]" : "text-red-400"}`}>{money(v.profit)}</span>
              </div>
            </button>
            {openId === v.id && <CarDetail vehicle={v} token={token} onAuthError={onAuthError} onChange={load} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, testid, tone = "default", icon: Icon }) {
  const tones = { default: "text-white", ok: "text-green-400", warn: "text-yellow-400", danger: "text-red-400" };
  return (
    <div data-testid={testid} className="bg-[#0C0C0C] border border-white/10 rounded-xl p-3">
      <div className="flex items-center gap-1.5 text-white/45 text-[11px] uppercase tracking-wider"><Icon size={13} /> {label}</div>
      <div className={`font-display font-black text-xl mt-1 ${tones[tone]}`}>{value}</div>
    </div>
  );
}

function CarDetail({ vehicle, token, onAuthError, onChange }) {
  const [maint, setMaint] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ date: "", type: "Oil Change", cost: "", vendor: "", notes: "", days_out: "", mileage: "", remind_months: "", remind_miles: "" });
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const load = () => {
    setLoading(true);
    axios.get(`${API}/maintenance?vehicle_id=${vehicle.id}`, auth).then((r) => setMaint(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const add = async () => {
    if (!form.cost && form.cost !== 0) { toast.error("Enter a cost."); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/maintenance`, {
        vehicle_id: vehicle.id, date: form.date, type: form.type,
        cost: parseFloat(form.cost) || 0, vendor: form.vendor, notes: form.notes, days_out: parseInt(form.days_out) || 0,
        mileage: parseInt(form.mileage) || 0, remind_months: parseInt(form.remind_months) || 0, remind_miles: parseInt(form.remind_miles) || 0,
      }, auth);
      toast.success("Maintenance logged.");
      setForm({ date: "", type: "Oil Change", cost: "", vendor: "", notes: "", days_out: "", mileage: "", remind_months: "", remind_miles: "" });
      load(); onChange?.();
    } catch (err) {
      if (err.response?.status === 401) onAuthError?.(); else toast.error("Could not save entry.");
    } finally { setAdding(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this maintenance entry?")) return;
    try {
      await axios.delete(`${API}/maintenance/${id}`, auth);
      toast.success("Entry deleted."); load(); onChange?.();
    } catch { toast.error("Could not delete."); }
  };

  const cls = "w-full bg-transparent border-b border-white/15 focus:border-[#F2B705] outline-none py-1.5 text-sm rounded-none";

  return (
    <div className="border-t border-white/10 p-4 bg-[#0A0A0A]" data-testid={`ledger-detail-${vehicle.id}`}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4 text-sm">
        <div><div className="text-white/40 text-xs">Revenue</div><div className="text-green-400 font-bold">{money(vehicle.revenue)}</div></div>
        <div><div className="text-white/40 text-xs">Maintenance</div><div className="text-yellow-400 font-bold">{money(vehicle.maintenance_cost)}</div></div>
        <div><div className="text-white/40 text-xs">Lost Revenue</div><div className="text-yellow-400 font-bold">{money(vehicle.lost_revenue)}</div></div>
        <div><div className="text-white/40 text-xs">Purchase</div><div className="font-bold">{money(vehicle.purchase_price)}</div></div>
      </div>

      <h4 className="flex items-center gap-2 text-sm font-semibold text-white/70 mb-2"><Wrench size={14} /> Maintenance Log</h4>
      {loading ? <Loader2 className="animate-spin text-[#F2B705]" size={16} /> : (
        <div className="space-y-1.5 mb-4">
          {maint.length === 0 && <p className="text-white/30 text-sm">No maintenance logged for this vehicle.</p>}
          {maint.map((m) => (
            <div key={m.id} data-testid={`maint-row-${m.id}`} className="flex items-center justify-between gap-2 bg-[#0C0C0C] border border-white/10 rounded px-3 py-2 text-sm">
              <div className="min-w-0">
                <span className="text-[#F2B705] font-semibold">{m.type}</span> · {money(m.cost)}
                {m.days_out > 0 && <span className="text-white/40"> · {m.days_out}d out</span>}
                {m.mileage > 0 && <span className="text-white/40"> · {Number(m.mileage).toLocaleString()} mi</span>}
                <div className="text-white/40 text-xs">{m.date}{m.vendor && ` · ${m.vendor}`}{m.notes && ` · ${m.notes}`}</div>
                {(m.next_due_date || m.next_due_mileage > 0) && (
                  <div className="text-[#F2B705]/80 text-xs mt-0.5 flex items-center gap-1"><BellRing size={11} /> Next due{m.next_due_date ? ` ${m.next_due_date}` : ""}{m.next_due_mileage > 0 ? `${m.next_due_date ? " or" : ""} at ${Number(m.next_due_mileage).toLocaleString()} mi` : ""}</div>
                )}
              </div>
              <button data-testid={`maint-delete-${m.id}`} onClick={() => remove(m.id)} className="text-white/30 hover:text-red-400 shrink-0"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      <div className="bg-[#0C0C0C] border border-white/10 rounded-lg p-3" data-testid={`maint-add-form-${vehicle.id}`}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <div><label className="text-[11px] text-white/45">Date</label><input type="date" data-testid="maint-date" className={`${cls} [color-scheme:dark]`} value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
          <div><label className="text-[11px] text-white/45">Type</label>
            <select data-testid="maint-type" className={`${cls} appearance-none`} value={form.type} onChange={(e) => set("type", e.target.value)}>
              {MAINT_TYPES.map((t) => <option key={t} value={t} className="bg-[#0A0A0A]">{t}</option>)}
            </select>
          </div>
          <div><label className="text-[11px] text-white/45">Cost $</label><input type="number" data-testid="maint-cost" className={cls} value={form.cost} onChange={(e) => set("cost", e.target.value)} /></div>
          <div><label className="text-[11px] text-white/45">Vendor</label><input data-testid="maint-vendor" className={cls} value={form.vendor} onChange={(e) => set("vendor", e.target.value)} /></div>
          <div><label className="text-[11px] text-white/45">Days out of service</label><input type="number" data-testid="maint-days-out" className={cls} value={form.days_out} onChange={(e) => set("days_out", e.target.value)} /></div>
          <div><label className="text-[11px] text-white/45">Notes</label><input data-testid="maint-notes" className={cls} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          <div><label className="text-[11px] text-white/45">Odometer (miles)</label><input type="number" data-testid="maint-mileage" className={cls} value={form.mileage} onChange={(e) => set("mileage", e.target.value)} placeholder="e.g. 115000" /></div>
          <div><label className="text-[11px] text-white/45">Remind in (months)</label><input type="number" data-testid="maint-remind-months" className={cls} value={form.remind_months} onChange={(e) => set("remind_months", e.target.value)} placeholder="e.g. 3" /></div>
          <div><label className="text-[11px] text-white/45">Remind in (miles)</label><input type="number" data-testid="maint-remind-miles" className={cls} value={form.remind_miles} onChange={(e) => set("remind_miles", e.target.value)} placeholder="e.g. 5000" /></div>
        </div>
        <p className="text-white/35 text-[11px] mt-2">Tip: set "Remind in months" (e.g. 3) and/or "Remind in miles" (e.g. 5000) to get a Service Reminder here — and a text/email — when the next service is due. Mileage reminders use each car's odometer (set in the Fleet tab).</p>
        <button data-testid={`maint-add-${vehicle.id}`} onClick={add} disabled={adding} className="mt-3 flex items-center gap-1.5 bg-[#F2B705] text-black text-sm font-bold px-4 py-2 rounded-md hover:bg-white transition-colors disabled:opacity-60">
          {adding ? <Loader2 className="animate-spin" size={14} /> : <Plus size={14} />} Log Maintenance
        </button>
      </div>
    </div>
  );
}
